import React, { createContext, useContext, useState, useEffect } from 'react';
import { translations } from '../utils/translations';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  // Force German as default language
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    const savedLanguage = localStorage.getItem('preferredLanguage');
    // If no saved language, default to German
    if (!savedLanguage) {
      localStorage.setItem('preferredLanguage', 'de');
      return 'de';
    }
    // Only use saved language if it's valid
    return (savedLanguage === 'en' || savedLanguage === 'de') ? savedLanguage : 'de';
  });

  // Save language preference to localStorage and update HTML lang attribute when it changes
  useEffect(() => {
    localStorage.setItem('preferredLanguage', currentLanguage);
    // Update HTML lang attribute
    document.documentElement.lang = currentLanguage;
  }, [currentLanguage]);

  const changeLanguage = (language) => {
    if (language === 'en' || language === 'de') {
      setCurrentLanguage(language);
    }
  };

  const toggleLanguage = () => {
    setCurrentLanguage(prev => prev === 'en' ? 'de' : 'en');
  };

  // Helper function to get translated text
  const t = (key) => {
    if (!key) return '';
    
    const keys = key.split('.');
    let value = translations[currentLanguage];
    
    // Navigate through the nested object
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        // Translation not found in current language, try English fallback
        value = translations.en;
        for (const fallbackKey of keys) {
          if (value && typeof value === 'object' && fallbackKey in value) {
            value = value[fallbackKey];
          } else {
            // If not found in English either, return the key
            console.warn(`Translation not found for key: ${key}`);
            return key;
          }
        }
        return value;
      }
    }
    
    // Return the found value or the key as fallback
    return typeof value === 'string' ? value : key;
  };

  const value = {
    currentLanguage,
    changeLanguage,
    toggleLanguage,
    t,
    isGerman: currentLanguage === 'de',
    isEnglish: currentLanguage === 'en'
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
